import React from 'react';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  ExtractedResourceProvenance,
  type DocumentExtractionV2Approval,
} from './ExtractedResourceProvenance';
import type { DocumentExtractionReviewSurface } from './documentExtractionV2Analytics';
import {
  getExtractedMedicationDetails,
  getExtractedObservationValue,
  getExtractedResourceDate,
  type ExtractedFhirResource,
  type ReviewableResourceType,
} from './useUnreviewedExtractedResources';

const useStyles = makeStyles(theme => ({
  reviewCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(6),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  label: {
    color: theme.palette.text.hint,
    fontSize: theme.typography.caption.fontSize,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  value: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.body1.fontSize,
  },
}));

/**
 * Render the raw FHIR date string as a readable date, falling back to the raw
 * value if it cannot be parsed (e.g. a partial date).
 */
export const formatReviewDate = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
};

type ExtractedResourceReviewCardProps = {
  resourceType: ReviewableResourceType;
  resource: ExtractedFhirResource;
  /** Human-readable code/display label for the resource. */
  display: string;
  /** Clinician-friendly label for the resource type (e.g. "Medication"). */
  typeLabel: string;
  subjectId: string;
  /**
   * Hide the inline "Source document:" link rendered by the provenance
   * controls. Set when the consumer renders the source itself (side-by-side).
   */
  hideSourceDocumentLink?: boolean;
  reviewAnalyticsSurface?: DocumentExtractionReviewSurface;
  onApproved?: (approval: DocumentExtractionV2Approval) => void;
  onRejected?: (resourceId: string) => void;
};

/**
 * A single extracted-resource review card: the clinician-friendly details of
 * what Zori extracted (type, code/display, medication dosage, value, date)
 * paired with the approve/reject + provenance controls. Shared between the Zori
 * Pulse review queue (`ExtractedResourceReviewModal`) and the "Analyze with
 * Zori AI" extraction modal so both surfaces present extractions identically.
 */
export const ExtractedResourceReviewCard: React.FC<
  ExtractedResourceReviewCardProps
> = ({
  resourceType,
  resource,
  display,
  typeLabel,
  subjectId,
  hideSourceDocumentLink,
  reviewAnalyticsSurface,
  onApproved,
  onRejected,
}) => {
  const classes = useStyles();

  const observationValue = getExtractedObservationValue(resource);
  const resourceDate = getExtractedResourceDate(resource);
  const medicationDetails =
    resourceType === 'MedicationStatement' ||
    resourceType === 'MedicationRequest'
      ? getExtractedMedicationDetails(resource)
      : undefined;
  const medicationFields = (
    medicationDetails
      ? [
          { key: 'dosage', label: 'Dosage', value: medicationDetails.dosage },
          { key: 'dose', label: 'Dose', value: medicationDetails.dose },
          { key: 'timing', label: 'Timing', value: medicationDetails.timing },
          { key: 'route', label: 'Route', value: medicationDetails.route },
          { key: 'notes', label: 'Notes', value: medicationDetails.notes },
        ]
      : []
  ).filter((field): field is { key: string; label: string; value: string } =>
    Boolean(field.value),
  );

  return (
    <div
      className={classes.reviewCard}
      data-testid={`extracted-resource-review-item-${resourceType}-${resource.id}`}
    >
      <div className={classes.field}>
        <span className={classes.label}>Type</span>
        <span className={classes.value}>{typeLabel}</span>
      </div>
      <div className={classes.field}>
        <span className={classes.label}>Code / Display</span>
        <span className={classes.value}>{display}</span>
      </div>
      {medicationFields.map(field => (
        <div key={field.key} className={classes.field}>
          <span className={classes.label}>{field.label}</span>
          <span
            className={classes.value}
            data-testid={`extracted-resource-review-${field.key}-${resourceType}-${resource.id}`}
          >
            {field.value}
          </span>
        </div>
      ))}
      {observationValue ? (
        <div className={classes.field}>
          <span className={classes.label}>Value</span>
          <span
            className={classes.value}
            data-testid={`extracted-resource-review-value-${resourceType}-${resource.id}`}
          >
            {observationValue}
          </span>
        </div>
      ) : null}
      {resourceDate ? (
        <div className={classes.field}>
          <span className={classes.label}>Date</span>
          <span
            className={classes.value}
            data-testid={`extracted-resource-review-date-${resourceType}-${resource.id}`}
          >
            {formatReviewDate(resourceDate)}
          </span>
        </div>
      ) : null}
      <ExtractedResourceProvenance
        resource={resource}
        resourceType={resourceType}
        subjectId={subjectId}
        hideSourceDocumentLink={hideSourceDocumentLink}
        reviewAnalyticsSurface={reviewAnalyticsSurface}
        onApproved={onApproved}
        onRejected={onRejected}
      />
    </div>
  );
};
